import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Alert, Button, Card, Col, Dropdown, Grid, message, Modal, Popconfirm, Row, Space, Typography} from 'antd';
import {
    DeleteOutlined,
    DownloadOutlined,
    EllipsisOutlined,
    PlusOutlined,
    ReloadOutlined,
    UploadOutlined
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import AgGrid from './common/AgGrid';
import {
    createWordData,
    createWordDataBatch,
    deleteWordData,
    deleteWordDataBatch,
    getWordDataList
} from '../api/wordDataApi';
import ExcelUploadModal from './ExcelUploadModal';
import WordDataCreateModal from './WordDataCreateModal';
import SearchComponent from './SearchComponent';
import {AG_GRID_LOCALE_TEXT} from '../config/agGridConfig';
import useApiPermission from '../hooks/useApiPermission';
import PermissionButton from './common/PermissionButton';
import PermissionGate from './common/PermissionGate';

const { Title } = Typography;

function WordDataList() {
  const [rowData, setRowData] = useState([]);
  const [, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [searchColumn, setSearchColumn] = useState('all');
  const [deleting, setDeleting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [isExcelModalOpen, setIsExcelModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const gridRef = useRef();
  const screens = Grid.useBreakpoint();
  const isXs = !screens.md;

  // API 권한 기반 버튼 제어 (API 함수의 _perm 메타데이터 사용)
  const { canCall, loading: permLoading } = useApiPermission();
  const canRead = canCall(getWordDataList);
  const canCreate = canCall(createWordData);
  const canUpload = canCall(createWordDataBatch);
  // 배치 삭제는 단건 삭제 권한으로 판정 (deleteWordData의 _perm 사용)
  const canDeleteBatch = canCall(deleteWordData);

  const searchOptions = [
    { value: 'all', label: '전체검색' },
    { value: 'wordNm', label: '단어명' },
    { value: 'wordEngAbbrNm', label: '영문약어' },
    { value: 'wordEngNm', label: '영문명' },
    { value: 'wordExpln', label: '설명' },
    { value: 'domainClsfNm', label: '도메인' },
    { value: 'synm', label: '동의어' },
    { value: 'phwrd', label: '금칙어' },
    { value: 'rgtr', label: '등록자' },
    { value: 'mdfr', label: '수정자' },
  ];

  // 날짜 포맷팅 함수
  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  };

  // AG Grid 컬럼 정의
  const agGridColumnDefs = [
    {
      headerName: '',
      field: 'checkbox',
      headerCheckboxSelection: true,
      headerCheckboxSelectionFilteredOnly: true,
      checkboxSelection: true,
      width: 33,
      pinned: 'left',
      lockPosition: true,
      suppressMenu: true,
      sortable: false,
      filter: false,
      resizable: false,
      suppressExport: true,
    },
    { headerName: '단어데이터ID', field: 'wordDataId', hide: true, width: 100 },
    { headerName: '단어명', field: 'wordNm', width: 100},
    { headerName: '영문약어', field: 'wordEngAbbrNm', width: 100 },
    { headerName: '영문명', field: 'wordEngNm', minWidth: 100, flex: 0.2},
    { headerName: '설명', field: 'wordExpln', minWidth: 200, flex: 0.8},
    { headerName: '도메인', field: 'domainClsfNm', width: 100 },
    { headerName: '동의어', field: 'synm', width: 100 },
    { headerName: '금칙어', field: 'phwrd', width: 100 },
    { headerName: '등록자', field: 'rgtr', cellStyle: { 'text-align': 'center' }, width: 100 },
    { headerName: '등록일시', field: 'regDt', sort: 'desc', sortable: true, cellStyle: { 'text-align': 'center' }, valueFormatter: (params) => formatDateTime(params.value), width: 120 },
    { headerName: '수정자', field: 'mdfr', cellStyle: { 'text-align': 'center' }, width: 100 },
    { headerName: '수정일시', field: 'mdfcnDt', cellStyle: { 'text-align': 'center' }, valueFormatter: (params) => formatDateTime(params.value), width: 120 },
  ];

  const agGridDefaultColDef = {
    sortable: true,
    filter: true,
    resizable: true,
  };

  // 검색 조건에 따른 필터링 함수
  const getFilteredData = () => {
    if (!searchText.trim()) return rowData;
    const searchValue = searchText.toLowerCase();
    return rowData.filter(item => {
      const by = (f) => item[f] && item[f].toLowerCase().includes(searchValue);
      switch (searchColumn) {
        case 'wordNm': return by('wordNm');
        case 'wordEngAbbrNm': return by('wordEngAbbrNm');
        case 'wordEngNm': return by('wordEngNm');
        case 'wordExpln': return by('wordExpln');
        case 'domainClsfNm': return by('domainClsfNm');
        case 'synm': return by('synm');
        case 'phwrd': return by('phwrd');
        case 'rgtr': return by('rgtr');
        case 'mdfr': return by('mdfr');
        case 'all':
        default:
          return (
            by('wordNm') || by('wordEngAbbrNm') || by('wordEngNm') || by('wordExpln') || by('domainClsfNm') || by('synm') || by('phwrd') || by('rgtr') || by('mdfr')
          );
      }
    });
  };

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (permLoading) return; // 권한 로딩 중엔 대기
      if (!canRead) {
        setError('목록 조회 권한이 없습니다.');
        return;
      }
      const data = await getWordDataList();
      if (data && Array.isArray(data.data)) {
        setRowData(data.data);
      } else {
        setError('응답 데이터 형식이 올바르지 않습니다.');
      }
    } catch (err) {
      setError('데이터를 가져오는 데 실패했습니다: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [permLoading, canRead]);


  // XLSX 다운로드 (SheetJS): 첫 열에 UUID(wordDataId), 표시 컬럼 순서 일치
  const handleDownloadExcel = () => {
    if (!gridRef.current || !gridRef.current.api) { message.warning('그리드가 아직 준비되지 않았습니다.'); return; }

    const displayed = gridRef.current.columnApi
      ? gridRef.current.columnApi.getAllDisplayedColumns().map(col => {
          const def = col.getColDef();
          return { field: def.field, headerName: def.headerName || def.field, suppressExport: !!def.suppressExport };
        }).filter(c => c.field && !c.suppressExport && c.field !== 'wordDataId')
      : agGridColumnDefs.filter(c => c.field && !c.suppressExport && c.field !== 'wordDataId').map(c => ({ field: c.field, headerName: c.headerName || c.field }));

    const header = ['UUID', ...displayed.map(c => c.headerName)];
    const rowsAoA = [header];

    gridRef.current.api.forEachNodeAfterFilterAndSort((node) => {
      const dataRow = [node.data?.wordDataId ?? ''];
      displayed.forEach(c => {
        let val = node.data?.[c.field];
        if (c.field === 'regDt' || c.field === 'mdfcnDt') val = formatDateTime(val);
        dataRow.push(val ?? '');
      });
      rowsAoA.push(dataRow);
    });

    const ws = XLSX.utils.aoa_to_sheet(rowsAoA);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Words');
    XLSX.writeFile(wb, 'word-data.xlsx');
  };

  // AG Grid용 삭제 함수
  const deleteSelectedRowsAgGrid = async () => {
    if (permLoading || !canDeleteBatch) { message.warning('삭제 권한이 없습니다.'); return; }
    if (!gridRef.current || !gridRef.current.api) {
      message.warning('그리드가 아직 초기화되지 않았습니다. 잠시 후 다시 시도해주세요.');
      return;
    }

    const selectedNodes = gridRef.current.api.getSelectedNodes();
    if (selectedNodes.length === 0) {
      message.warning('삭제할 항목을 선택해주세요.');
      return;
    }

    const validSelectedNodes = selectedNodes.filter(node => node.data && node.data.wordDataId);
    const selectedIds = validSelectedNodes.map(node => node.data.wordDataId);

    if (selectedIds.length === 0) {
      message.warning('삭제할 항목을 선택해주세요.');
      return;
    }

    setDeleting(true);
    try {
      await deleteWordDataBatch(selectedIds);
      await fetchList();
      if (gridRef.current && gridRef.current.api) {
        gridRef.current.api.deselectAll();
      }
      message.success(`${selectedIds.length}개 항목이 삭제되었습니다.`);
    } catch (err) {
      // 에러 토스트는 공통 인터셉터에서 처리
    } finally {
      setDeleting(false);
    }
  };

  const handleExcelUploadSuccess = () => fetchList();
  const handleCreateSuccess = () => fetchList();

  // 데이터 로드
  useEffect(() => { fetchList(); }, [fetchList]);

  if (error) return <Alert message="일시적인 오류가 발생했습니다." description={error} type="error" showIcon />;

  // 작은 화면 드롭다운에서 사용할 삭제 확인 모달
  const confirmDeleteFromMenu = () => {
    if (permLoading || !canDeleteBatch) { message.warning('삭제 권한이 없습니다.'); return; }
    if (!gridRef.current || !gridRef.current.api) {
      message.warning('그리드가 아직 초기화되지 않았습니다. 잠시 후 다시 시도해주세요.');
      return;
    }
    const selectedNodes = gridRef.current.api.getSelectedNodes();
    if (selectedNodes.length === 0) {
      message.warning('삭제할 항목을 선택해주세요.');
      return;
    }
    Modal.confirm({ title: '선택된 항목 삭제', content: '선택된 항목들을 삭제하시겠습니까?', okText: '삭제', okButtonProps: { danger: true }, cancelText: '취소', onOk: deleteSelectedRowsAgGrid });
  };

  const actionMenuItems = [
    ...(canCreate ? [{ key: 'create', icon: <PlusOutlined />, label: '신규생성' }] : []),
    ...(canDeleteBatch ? [{ type: 'divider' }, { key: 'delete', icon: <DeleteOutlined />, label: '선택 항목 삭제', danger: true }] : []),
    ...(canUpload ? [{ type: 'divider' }, { key: 'upload', icon: <UploadOutlined />, label: '엑셀 업로드' }] : []),
    { key: 'download', icon: <DownloadOutlined />, label: '엑셀 다운로드' },
    ...(canRead ? [{ type: 'divider' }, { key: 'refresh', icon: <ReloadOutlined />, label: '새로고침' }] : []),
  ];

  const onActionMenuClick = ({ key }) => {
    switch (key) {
      case 'create':
        if (permLoading || !canCreate) { message.warning('생성 권한이 없습니다.'); break; }
        setIsCreateModalOpen(true);
        break;
      case 'delete':
        confirmDeleteFromMenu();
        break;
      case 'upload':
        if (permLoading || !canUpload) { message.warning('업로드 권한이 없습니다.'); break; }
        setIsExcelModalOpen(true);
        break;
      case 'refresh':
        if (permLoading || !canRead) { message.warning('조회 권한이 없습니다.'); break; }
        fetchList();
        break;
      case 'download':
        handleDownloadExcel();
        break;
      default:
        break;
    }
  };

  return (
    <PermissionGate require={getWordDataList}>
      {({allowed}) => (
        <Card style={{ opacity: allowed ? 1 : 0.5, pointerEvents: allowed ? 'auto' : 'none' }}>
          <Title level={2}>단어 목록</Title>

          {successMessage && (
            <Alert message="성공" description={successMessage} type="success" showIcon closable onClose={() => setSuccessMessage('')} style={{ marginBottom: 16 }} />
          )}

          <Card style={{ marginBottom: 16 }}>
            <Row gutter={[8, 8]} align="middle">
              <Col xs={24}>
                {isXs ? (
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center', width: '100%' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <SearchComponent
                        searchColumn={searchColumn}
                        setSearchColumn={setSearchColumn}
                        searchText={searchText}
                        setSearchText={setSearchText}
                        searchOptions={searchOptions}
                        defaultSearchColumn={'wordNm'}
                      />
                    </div>
                    <div style={{ flexShrink: 0 }}>
                      <Dropdown menu={{ items: actionMenuItems, onClick: onActionMenuClick }} placement="bottomRight" trigger={["click"]}>
                        <Button aria-label="작업 메뉴" icon={<EllipsisOutlined />} />
                      </Dropdown>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', width: '100%' }}>
                    <div style={{ flex: '1 1 300px', minWidth: 0 }}>
                      <SearchComponent
                        searchColumn={searchColumn}
                        setSearchColumn={setSearchColumn}
                        searchText={searchText}
                        setSearchText={setSearchText}
                        searchOptions={searchOptions}
                        defaultSearchColumn={'wordNm'}
                      />
                    </div>
                    <div style={{ flex: '0 0 auto' }}>
                      <Space wrap>
                        <PermissionButton type="primary" icon={<PlusOutlined />} onClick={() => (permLoading || !canCreate) ? message.warning('생성 권한이 없습니다.') : setIsCreateModalOpen(true)} disabled={permLoading || !canCreate} require={createWordData}>
                          신규생성
                        </PermissionButton>
                        <Popconfirm title="선택된 항목 삭제" description="선택된 항목을 삭제하시겠습니까?" onConfirm={deleteSelectedRowsAgGrid} okText="삭제" cancelText="취소" disabled={permLoading || !canDeleteBatch}>
                          <PermissionButton danger icon={<DeleteOutlined />} disabled={permLoading || !canDeleteBatch || deleting} loading={deleting} require={deleteWordData}>선택 항목 삭제</PermissionButton>
                        </Popconfirm>
                        <PermissionButton type="primary" icon={<UploadOutlined />} onClick={() => (permLoading || !canUpload) ? message.warning('업로드 권한이 없습니다.') : setIsExcelModalOpen(true)} disabled={permLoading || !canUpload} require={createWordDataBatch} style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}>
                          엑셀 업로드
                        </PermissionButton>
                        <Button type="primary" icon={<DownloadOutlined />} onClick={handleDownloadExcel} style={{ backgroundColor: '#1890ff', borderColor: '#1890ff' }}>엑셀 다운로드</Button>
                        <PermissionButton icon={<ReloadOutlined />} onClick={() => (permLoading || !canRead) ? message.warning('조회 권한이 없습니다.') : fetchList()} disabled={permLoading || !canRead} require={getWordDataList}>새로고침</PermissionButton>
                      </Space>
                    </div>
                  </div>
                )}
              </Col>
            </Row>
          </Card>

          <div style={{ width: '100%' }}>
            <AgGrid
              ref={gridRef}
              rowData={getFilteredData()}
              columnDefs={agGridColumnDefs}
              defaultColDef={agGridDefaultColDef}
              paginationPageSize={20}
              rowSelection="multiple"
              localeText={AG_GRID_LOCALE_TEXT}
            />
          </div>

          <ExcelUploadModal
            isOpen={isExcelModalOpen}
            onClose={() => setIsExcelModalOpen(false)}
            onSuccess={handleExcelUploadSuccess}
            agGridColumnDefs={agGridColumnDefs}
            agGridDefaultColDef={agGridDefaultColDef}
            fieldMapping={['wordNm','wordEngAbbrNm','wordEngNm','wordExpln','domainClsfNm','synm','phwrd']}
            uploadApi={createWordDataBatch}
            excelGuide={'엑셀 파일의 첫 번째 행은 헤더로 인식되며, 두 번째 행부터 데이터를 읽어옵니다. A열: *단어명 | B열: *영문약어 | C열: *영문명 | D열: *설명 | E열: 도메인 | F열: 동의어 | G열: 금칙어'}
            rowKey="wordDataId"
          />

          <WordDataCreateModal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} onSuccess={handleCreateSuccess} />
        </Card>
      )}
    </PermissionGate>
  );
}

export default WordDataList;
